/**
 * What each exercise asks of the body, beyond the muscle it trains (B-45, D-101).
 *
 * A plan removes every exercise that loads a joint the person has told us about, and a
 * red-flag condition removes every high-intensity one (`plan.ts`). This table is what
 * makes those removals possible, and it is the one place a mistake in the permissive
 * direction keeps a squat in the plan of someone with a bad knee — so it is deliberately
 * generous with areas: an exercise is tagged with a joint when that joint is loaded
 * meaningfully, not only when it is the target.
 *
 * It is separate from the catalog on purpose. The catalog's names, factors and duration
 * tags are transcribed from §8 of the 2026-09-02 spec and must not diverge from it; this
 * is new information with no source there, and folding it in would put an unsourced
 * column into a transcribed table. `strain.test.ts` holds the two together: every
 * catalog id has an entry and every entry is a catalog id, so adding an exercise without
 * tagging it fails CI.
 *
 * Areas, abbreviated in the rows below:
 *   k knee · b lower back · s shoulder · w wrist/elbow · a ankle/foot · h hip · n neck
 * Intensity: L low · M moderate · H high. `impact` marks jumping, running and landing.
 */
const AREA = {
    k: "knee",
    b: "lowerBack",
    s: "shoulder",
    w: "wristElbow",
    a: "ankleFoot",
    h: "hip",
    n: "neck",
};
const INTENSITY = { L: "low", M: "moderate", H: "high" };
const ROWS = [
    // Chest
    ["flat-barbell-bench-press", "s w", "H"],
    ["incline-barbell-bench-press", "s w", "H"],
    ["decline-barbell-bench-press", "s w", "H"],
    ["flat-dumbbell-press", "s w", "M"],
    ["incline-dumbbell-press", "s w", "M"],
    ["decline-dumbbell-press", "s w", "M"],
    ["machine-chest-press", "s w", "L"],
    ["smith-machine-bench-press", "s w", "M"],
    ["incline-smith-machine-bench-press", "s w", "M"],
    ["decline-smith-machine-bench-press", "s w", "M"],
    ["flat-dumbbell-fly", "s w", "M"],
    ["incline-dumbbell-fly", "s w", "M"],
    ["high-to-low-cable-fly", "s", "L"],
    ["mid-cable-fly", "s", "L"],
    ["low-to-high-cable-fly", "s", "L"],
    ["incline-cable-fly", "s", "L"],
    ["pec-deck", "s", "L"],
    ["push-up", "s w", "M"],
    // Kneeling puts the body's weight through the kneecap.
    ["knee-push-up", "s w k", "L"],
    ["weighted-push-up", "s w", "M"],
    ["decline-push-up", "s w", "M"],
    ["chest-dips", "s w", "H"],
    // Back
    ["conventional-deadlift", "b h k w", "H"],
    ["sumo-deadlift", "b h k w", "H"],
    ["barbell-row", "b w", "M"],
    ["close-grip-bent-over-barbell-row", "b w", "M"],
    ["pendlay-row", "b w", "H"],
    ["single-arm-dumbbell-row", "w", "M"],
    ["t-bar-row", "b w", "M"],
    ["chest-supported-dumbbell-row", "w", "L"],
    ["seated-cable-row", "w", "L"],
    ["plate-loaded-machine-row", "w", "L"],
    ["machine-high-row", "s w", "L"],
    ["machine-low-row", "w", "L"],
    ["pull-up", "s w", "H"],
    ["chin-up", "s w", "H"],
    ["weighted-pull-up", "s w", "H"],
    ["assisted-pull-up", "s w", "M"],
    ["wide-grip-lat-pulldown", "s w", "L"],
    ["wide-grip-behind-neck-lat-pulldown", "s w n", "M"],
    ["close-grip-lat-pulldown", "s w", "L"],
    ["v-bar-lat-pulldown", "s w", "L"],
    ["neutral-grip-lat-pulldown", "s w", "L"],
    ["single-arm-lat-pulldown", "s w", "L"],
    ["straight-arm-pulldown", "s w", "L"],
    ["dumbbell-pullover", "s w", "M"],
    ["rack-pull", "b h w", "H"],
    ["face-pull", "s", "L"],
    // Shoulders
    ["standing-barbell-overhead-press", "s w b n", "H"],
    ["seated-barbell-overhead-press", "s w n", "M"],
    ["seated-dumbbell-shoulder-press", "s w", "M"],
    ["arnold-press", "s w", "M"],
    ["machine-shoulder-press", "s w", "L"],
    ["lateral-side-raise", "s", "L"],
    ["cable-lateral-raise", "s", "L"],
    ["machine-lateral-raise", "s", "L"],
    ["dumbbell-front-raise", "s", "L"],
    ["cable-front-raise", "s", "L"],
    ["plate-front-raise", "s w", "L"],
    // Bent over, unsupported.
    ["dumbbell-rear-delt-fly", "s b", "L"],
    ["cable-rear-delt-fly", "s", "L"],
    ["reverse-pec-deck", "s", "L"],
    ["upright-row", "s w n", "M"],
    ["barbell-shrug", "n w", "M"],
    ["dumbbell-shrug", "n w", "M"],
    ["landmine-press", "s w", "M"],
    // Biceps — every curl loads the elbow and the wrist, and nothing else much.
    ["barbell-curl", "w", "L"],
    ["ez-bar-curl", "w", "L"],
    ["standing-dumbbell-curl", "w", "L"],
    ["seated-dumbbell-curl", "w", "L"],
    ["alternating-dumbbell-curl", "w", "L"],
    ["incline-dumbbell-curl", "w s", "L"],
    ["hammer-curl", "w", "L"],
    ["barbell-preacher-curl", "w", "L"],
    ["dumbbell-preacher-curl", "w", "L"],
    ["machine-preacher-curl", "w", "L"],
    ["concentration-curl", "w", "L"],
    ["straight-bar-cable-curl", "w", "L"],
    ["rope-cable-curl", "w", "L"],
    ["rope-hammer-curl", "w", "L"],
    ["single-arm-cable-curl", "w", "L"],
    ["spider-curl", "w", "L"],
    ["machine-bicep-curl", "w", "L"],
    // Triceps
    ["close-grip-bench-press", "s w", "H"],
    ["ez-bar-skull-crusher", "w s", "M"],
    ["dumbbell-skull-crusher", "w s", "M"],
    ["dumbbell-overhead-tricep-extension", "s w", "M"],
    ["cable-rope-overhead-tricep-extension", "s w", "L"],
    ["rope-tricep-pushdown", "w", "L"],
    ["straight-bar-tricep-pushdown", "w", "L"],
    ["v-bar-tricep-pushdown", "w", "L"],
    ["reverse-grip-tricep-pushdown", "w", "L"],
    ["tricep-dips", "s w", "H"],
    ["tricep-kickback", "w", "L"],
    ["machine-tricep-extension", "w", "L"],
    ["bench-dips", "s w", "M"],
    ["diamond-push-up", "s w", "M"],
    // Forearms
    ["barbell-wrist-curl", "w", "L"],
    ["dumbbell-wrist-curl", "w", "L"],
    ["reverse-wrist-curl", "w", "L"],
    ["barbell-reverse-curl", "w", "L"],
    ["ez-bar-reverse-curl", "w", "L"],
    ["farmers-carry", "w b a", "M"],
    ["plate-pinch", "w", "L"],
    ["dead-hang", "s w", "L"],
    // Quads
    ["back-squat", "k h b", "H"],
    ["front-squat", "k h b w", "H"],
    ["goblet-squat", "k h", "M"],
    ["hack-squat", "k h", "M"],
    ["leg-press", "k h", "M"],
    ["smith-machine-squat", "k h b", "M"],
    ["bulgarian-split-squat", "k h a", "M"],
    ["walking-lunge", "k h a", "M"],
    ["reverse-lunge", "k h", "M"],
    ["step-up", "k h a", "M"],
    ["leg-extension", "k", "L"],
    ["sissy-squat", "k", "M"],
    ["pendulum-squat", "k h", "M"],
    // Hamstrings
    ["barbell-romanian-deadlift", "b h w", "M"],
    ["dumbbell-romanian-deadlift", "b h", "M"],
    ["stiff-leg-deadlift", "b h w", "M"],
    ["lying-leg-curl", "k", "L"],
    ["seated-leg-curl", "k", "L"],
    ["nordic-hamstring-curl", "k", "H"],
    // The bar sits on the upper back and neck.
    ["good-morning", "b h n", "M"],
    ["glute-ham-raise", "k b", "M"],
    // Glutes
    ["barbell-hip-thrust", "h b", "M"],
    ["machine-hip-thrust", "h", "L"],
    ["glute-bridge", "h", "L"],
    ["cable-kickback", "h", "L"],
    ["cable-pull-through", "b h", "L"],
    ["hip-abduction-machine", "h", "L"],
    ["hip-adduction-machine", "h", "L"],
    ["kettlebell-swing", "b h w", "M"],
    // Calves
    ["standing-machine-calf-raise", "a", "L"],
    ["smith-machine-calf-raise", "a", "L"],
    ["dumbbell-standing-calf-raise", "a w", "L"],
    ["seated-calf-raise", "a", "L"],
    ["leg-press-calf-raise", "a", "L"],
    ["donkey-calf-raise", "a b", "L"],
    // Core
    ["plank", "s", "L"],
    ["weighted-plank", "s b", "M"],
    ["side-plank", "s", "L"],
    ["hanging-leg-raise", "s w h", "M"],
    ["hanging-knee-raise", "s w", "M"],
    ["lying-leg-raise", "b h", "L"],
    ["cable-crunch", "b", "L"],
    ["machine-crunch", "b", "L"],
    ["ab-wheel-rollout", "s b", "M"],
    ["decline-sit-up", "b h n", "M"],
    ["russian-twist", "b", "M"],
    ["cable-woodchop", "b s", "L"],
    ["pallof-press", "", "L"],
    ["dead-bug", "", "L"],
    // Flexes the spine like the cable crunch, unloaded.
    ["half-crunch", "b", "L"],
    ["mountain-climbers", "s w h k", "H", "impact"],
    ["back-extension-45", "b h", "M"],
    ["hyperextension", "b h", "M"],
    // Full body
    ["clean-and-press", "b s w k h n", "H"],
    ["power-clean", "b w k h", "H"],
    ["thruster", "k h s w b", "H"],
    ["sled-push", "k a h", "H"],
    ["sled-pull", "k a b", "H"],
    ["battle-ropes", "s w b", "H"],
    ["burpee", "k a w s b", "H", "impact"],
    ["box-jump", "k a h", "H", "impact"],
    ["rowing-machine", "b k w", "M"],
    ["assault-bike", "k s", "H"],
    ["ski-erg", "s b w", "M"],
    ["treadmill", "k a h", "M", "impact"],
    ["elliptical", "k h", "L"],
    ["cycle", "k", "L"],
];
export const STRAIN = Object.freeze(Object.fromEntries(ROWS.map(([id, areas, intensity, impact]) => [
    id,
    Object.freeze({
        areas: Object.freeze(areas.split(" ").filter(Boolean).map((code) => AREA[code])),
        intensity: INTENSITY[intensity],
        impact: impact === "impact",
    }),
])));
export function strainOf(id) {
    return STRAIN[id];
}
//# sourceMappingURL=strain.js.map